import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { OrderDetail, FlightType, Airport } from '../types';
import { FlightDetailModal } from './FlightDetailModal';
import { fetchAirportsData, getAirportDisplay } from '../helpers';
import { ReactComponent as FlightIcon } from 'app/assets/img/icon_plane.svg';
import { ReactComponent as ArrowRightIcon } from 'app/assets/img/right_arrow.svg';
import StyledCollapse, {
  CollapsePanelWrapper,
} from 'app/components/StyledCollapse';

interface FlightInfoSectionProps {
  order: OrderDetail;
}

export const FlightInfoSection: React.FC<FlightInfoSectionProps> = ({
  order,
}) => {
  const translations = useSelector(selectTranslations);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [airports, setAirports] = useState<Airport[]>([]);

  useEffect(() => {
    fetchAirportsData().then(setAirports);
  }, []);

  const firstItem = order.orderItems[0];
  if (!firstItem) return null;

  const { flight } = firstItem;

  const formatFlightDate = (datetime: string) => {
    const m = moment(datetime);
    const dayOfWeek = m.format('dd');
    return `${m.format('DD/MM/YYYY')}`;
  };

  const formatTime = (datetime: string) => {
    return moment(datetime).format('HH:mm');
  };

  const getFlightTypeLabel = () => {
    return order.flightType === FlightType.ROUND_TRIP
      ? translations?.['b2b.txt.flightTypeRoundtrip'] || 'Khứ hồi'
      : translations?.['b2b.txt.flightTypeOneway'] || 'Một chiều';
  };

  const getCabinClass = (cabinClass?: string) => {
    if (cabinClass?.toLowerCase().includes('economy')) return translations?.['b2b.txt.economyClass'] || 'Phổ thông';
    if (cabinClass?.toLowerCase().includes('business')) return translations?.['b2b.txt.businessClass'] || 'Thương gia';
    return translations?.['b2b.txt.economyClass'] || 'Phổ thông';
  };

  // Get all flights (departure and return if round trip)
  const flights = order.orderItems
    .filter(item => item.flight)
    .map(item => item.flight);

  return (
    <>
      <BoxContainerCollapse>
        <StyledCollapse defaultActiveKey="">
          <CollapsePanelWrapper
            className="collapseItem"
            key={1}
            header={
              <div className="boxLeftHeaderCollapse">
                <div className="boxIcon">
                  <FlightIcon width={20} height={20} />
                </div>
                <div>
                  <Title>
                    {getAirportDisplay(flight.departure, airports)}&nbsp;&nbsp;
                    <i
                      className={`fa-solid ${
                        order.flightType === FlightType.ROUND_TRIP
                          ? 'fa-arrow-right-arrow-left'
                          : 'fa-arrow-right'
                      }`}
                      style={{ fontSize: 12 }}
                    />
                    &nbsp;&nbsp;
                    {getAirportDisplay(flight.arrival, airports)}
                  </Title>
                  <SubTitle>{getFlightTypeLabel()}</SubTitle>
                </div>
              </div>
            }
          >
            <div className="boxContentCollapse">
              {flights.map((flightItem, index) => (
                <ItemLeg key={index}>
                  <div className="txtLeg">
                    {index === 0
                      ? (translations?.['b2b.txt.departureLabel'] || 'Chiều đi')
                      : (translations?.['b2b.txt.returnLabel'] || 'Chiều về')}
                  </div>
                  <div className="legContent">
                    <span>{formatFlightDate(flightItem.departureTime)}</span>
                    &nbsp;|&nbsp;
                    <span>{getCabinClass(flightItem.cabinClass)}</span>
                    &nbsp;|&nbsp;
                    <span>
                      {formatTime(flightItem.departureTime)}
                      &nbsp;
                      <i
                        className="fa-solid fa-arrow-right icon"
                        style={{ fontSize: 12 }}
                      />
                      &nbsp;
                      {formatTime(flightItem.arrivalTime)}
                    </span>
                  </div>
                </ItemLeg>
              ))}
              <div
                className="seeDetail"
                onClick={() => setShowDetailModal(true)}
              >
                {translations?.['b2b.txt.seeDetail'] || 'Chi tiết'} &nbsp;
                <ArrowRightIcon />
              </div>
            </div>
          </CollapsePanelWrapper>
        </StyledCollapse>
      </BoxContainerCollapse>

      <FlightDetailModal
        visible={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        order={order}
        airports={airports}
      />
    </>
  );
};

const BoxContainerCollapse = styled.div`
  .collapseItem {
    border-radius: 8px !important;
    border: 1px solid #e5e5e5;
    overflow: hidden;
    .ant-collapse-header {
      padding-right: 12px !important;
      padding-top: 0px !important;
      height: 50px;
      background: #f2f2f2;
      .ant-collapse-arrow {
        right: 10px !important;
      }
    }
  }
  .boxLeftHeaderCollapse {
    height: 50px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .boxIcon {
    background: var(--color-gradient);
    min-height: 50px;
    height: 100%;
    min-width: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .boxContentCollapse {
    background: #fff;
    padding: 10px;
  }
  .seeDetail {
    color: var(--color-4);
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-top: 8px;
    &:hover {
      opacity: 0.8;
    }
  }
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const SubTitle = styled.div`
  font-size: 10px;
`;

const ItemLeg = styled.div`
  display: flex;
  margin-bottom: 6px;
  font-size: 14px;
  align-items: center;
  .txtLeg {
    color: var(--color-9);
    min-width: 80px;
    font-weight: 600;
    flex-shrink: 0;
  }
  .legContent {
    display: inline;
    line-height: 1.4;
  }
  .icon {
    display: inline;
    margin: 0 4px;
    font-size: 12px;
    vertical-align: middle;
  }
`;
